import React, { useState } from "react";
import {
  ArrowBack,
  Description,
  Upload,
  Security,
  ExpandMore,
  ExpandLess,
  Star,
  VisibilityOutlined
} from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
  Collapse,
  CircularProgress,
  Alert
} from "@mui/material";
import { msalInstance } from "common/config/auth.config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ApiResult } from "common/models";
import axios from "axios";
import "./BatchExtractionResults.scss";

export type ExtractedData = {
  [key: string]: string | number;
};

// Types for Quality Check Report API
export interface QualityCheckReportResponse {
  batchGuid: string;
  finalReport: {
    batch_summary: {
      batch_id: string;
      total_documents: number;
      passed_documents: number;
      failed_documents: number;
      pass_percentage: number;
    };
    document_type_summary: Array<{
      document_type: string;
      total_documents: number;
      passed_documents: number;
      failed_documents: number;
      pass_percentage: number;
      field_summary: Array<{
        field_name: string;
        total_documents: number;
        passed_documents: number;
        failed_documents: number;
        pass_percentage: number;
        failure_comments: string | null;
        failure_summary: {
          summary: string;
          common_patterns: string[];
          root_cause: string;
          recommendations: string[];
        } | null;
      }>;
      document_summary: Array<{
        doc_id: number;
        document_name: string;
        total_fields: number;
        passed_fields: number;
        failed_fields: number;
        pass_percentage: number;
        status: string;
        failures: Array<{
          field_name: string;
          field_value: string;
          failed_rule: string;
          failure_comment: string;
        }>;
      }>;
    }>;
    executive_summary: string;
    key_findings: string[];
    overall_recommendations: string[];
    next_actions: string[];
  };
}

// API Service for Quality Check
class QualityCheckService {
  public static async triggerQualityCheck(
    batchGUID: string
  ): Promise<ApiResult<any>> {
    try {
      // Get authentication token
      const request = {
        scopes: [process.env.REACT_APP_API_SCOPE || ""]
      };

      let tokenResult;
      try {
        tokenResult = await msalInstance.acquireTokenSilent(request);
      } catch (error: any) {
        if (error instanceof InteractionRequiredAuthError) {
          tokenResult = await msalInstance.acquireTokenPopup(request);
        } else {
          throw error;
        }
      }

      const engApiUrl = process.env.REACT_APP_ENG_API_URL;
      const fullUrl = `${engApiUrl}/api/Documents/TriggerQualityCheck/${batchGUID}`;

      // Make the HTTP POST request with authentication
      const axiosResult = await axios.post(
        fullUrl,
        { batchGUID }, // Send batchGUID as payload
        {
          headers: {
            Authorization: `Bearer ${tokenResult.accessToken}`,
            "Content-Type": "application/json"
          }
        }
      );

      return new ApiResult<any>(axiosResult.data, axiosResult.status);
    } catch (error: any) {
      console.error("Error in triggerQualityCheck:", error);
      return new ApiResult<any>(
        null as any,
        error.response?.status || 500,
        error.response?.data?.message ||
          error.message ||
          "Failed to trigger quality check"
      );
    }
  }

  public static async fetchQualityCheckReport(
    batchGUID: string
  ): Promise<ApiResult<QualityCheckReportResponse>> {
    try {
      // Get authentication token
      const request = {
        scopes: [process.env.REACT_APP_API_SCOPE || ""]
      };

      let tokenResult;
      try {
        tokenResult = await msalInstance.acquireTokenSilent(request);
      } catch (error: any) {
        if (error instanceof InteractionRequiredAuthError) {
          tokenResult = await msalInstance.acquireTokenPopup(request);
        } else {
          throw error;
        }
      }

      const engApiUrl = process.env.REACT_APP_ENG_API_URL;
      const fullUrl = `${engApiUrl}/api/Documents/FetchQualityCheckReport/${batchGUID}`;

      // Make the HTTP GET request with authentication
      const axiosResult = await axios.get(fullUrl, {
        headers: {
          Authorization: `Bearer ${tokenResult.accessToken}`,
          "Content-Type": "application/json"
        }
      });

      return new ApiResult<QualityCheckReportResponse>(
        axiosResult.data,
        axiosResult.status
      );
    } catch (error: any) {
      console.error("Error in fetchQualityCheckReport:", error);
      return new ApiResult<QualityCheckReportResponse>(
        null as any,
        error.response?.status || 500,
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch quality check report"
      );
    }
  }
}

type BatchExtractionResultsProps = {
  batchData: Record<string, Record<string, ExtractedData>>;
  categoryCounts: Record<string, number>;
  batchGUID?: string;
  onBack: () => void;
  onNewUpload: () => void;
  onRunBatchQualityCheck: () => void;
};

export { QualityCheckService };

export const BatchExtractionResults: React.FC<BatchExtractionResultsProps> = ({
  batchData,
  categoryCounts,
  batchGUID,
  onBack,
  onNewUpload,
  onRunBatchQualityCheck
}) => {
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const [expandedFiles, setExpandedFiles] = useState<Record<string, boolean>>(
    {}
  );

  // Loading and error states for quality check API
  const [isQualityCheckLoading, setIsQualityCheckLoading] = useState(false);
  const [qualityCheckError, setQualityCheckError] = useState<string | null>(
    null
  );

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleFile = (key: string) => {
    setExpandedFiles((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const totalCategories = Object.keys(batchData).length;
  const totalFiles = Object.values(categoryCounts).reduce(
    (sum, count) => sum + count,
    0
  );
  const totalFields = Object.values(batchData).reduce((sum, categoryData) => {
    return (
      sum +
      Object.values(categoryData).reduce((fileSum, fileData) => {
        return fileSum + Object.keys(fileData).length;
      }, 0)
    );
  }, 0);

  const categoryColors: Record<string, string> = {
    Contract: "#3b82f6",
    Invoice: "#10b981",
    Receipt: "#f59e0b",
    Report: "#8b5cf6",
    Agreement: "#ef4444"
  };

  /**
   * Handle View Batch Quality Check button click
   * Directly navigates to quality check results without API call
   */
  const handleViewQualityCheck = async () => {
    // if (!batchGUID) {
    //   console.error("No batch GUID available for quality check");
    //   return;
    // }

    try {
      // Call the parent handler to navigate to results
      onRunBatchQualityCheck();
    } catch (error: any) {
      console.error("Error navigating to quality check:", error);
    }
  };

  /**
   * Handle Run Batch Quality Check button click
   * Makes API call to trigger quality check and then calls parent handler
   */
  const handleRunQualityCheck = async () => {
    if (!batchGUID) {
      setQualityCheckError("No batch GUID available for quality check");
      return;
    }

    setIsQualityCheckLoading(true);
    setQualityCheckError(null);

    try {
      const response = await QualityCheckService.triggerQualityCheck(batchGUID);

      if (!response.hasErrors) {
        // Call the parent handler to navigate to results
        onRunBatchQualityCheck();
      } else {
        setQualityCheckError(
          response.errors?.[0] || "Failed to trigger quality check"
        );
      }
    } catch (error: any) {
      console.error("Error triggering quality check:", error);
      setQualityCheckError("An error occurred while triggering quality check");
    } finally {
      setIsQualityCheckLoading(false);
    }
  };

  return (
    <div className="batch-extraction-results">
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <div className="header-content">
            <Typography variant="h4" className="title">
              Batch Extraction Results
            </Typography>
            <Typography variant="body2" className="subtitle">
              Extracted parameters from {totalFiles} documents across{" "}
              {totalCategories} categories
            </Typography>
          </div>
        </div>
        <div className="header-actions">
          <Button
            variant="contained"
            onClick={handleViewQualityCheck}
            startIcon={<VisibilityOutlined />}
            className="primary-button"
          >
            VIEW Batch Quality Check
          </Button>
          <Button
            variant="contained"
            onClick={handleRunQualityCheck}
            startIcon={
              isQualityCheckLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Security />
              )
            }
            className="primary-button"
            disabled={isQualityCheckLoading || !batchGUID}
          >
            {isQualityCheckLoading
              ? "Running Quality Check..."
              : "Run Batch Quality Check"}
          </Button>
        </div>
      </div>

      {/* Error Alert for Quality Check */}
      {qualityCheckError && (
        <Alert
          severity="error"
          onClose={() => setQualityCheckError(null)}
          sx={{ mb: 2 }}
        >
          {qualityCheckError}
        </Alert>
      )}

      {/* Summary Stats */}
      <div className="summary-stats">
        <Card className="stat-card">
          <CardContent className="stat-content">
            <div className="stat-icon categories">
              <Description />
            </div>
            <div className="stat-info">
              <Typography variant="body2" className="stat-label">
                Total Categories
              </Typography>
              <Typography variant="h6" className="stat-value">
                {totalCategories}
              </Typography>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="stat-content">
            <div className="stat-icon documents">
              <Description />
            </div>
            <div className="stat-info">
              <Typography variant="body2" className="stat-label">
                Total Documents
              </Typography>
              <Typography variant="h6" className="stat-value">
                {totalFiles}
              </Typography>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="stat-content">
            <div className="stat-icon fields">
              <Star />
            </div>
            <div className="stat-info">
              <Typography variant="body2" className="stat-label">
                Total Fields Extracted
              </Typography>
              <Typography variant="h6" className="stat-value">
                {totalFields}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category-wise Results */}
      <div className="categories-section">
        {Object.entries(batchData).map(([category, categoryData]) => {
          const isExpanded = expandedCategories[category];
          const fileCount = categoryCounts[category] || 0;
          const fieldCount = Object.values(categoryData).reduce(
            (sum, fileData) => {
              return sum + Object.keys(fileData).length;
            },
            0
          );
          const color = categoryColors[category] || "#64748b";

          return (
            <Card key={category} className="category-card">
              {/* Category Header */}
              <div
                className="category-header"
                onClick={() => toggleCategory(category)}
                role="button"
                tabIndex={0}
              >
                <div className="category-info">
                  <div
                    className="category-icon"
                    style={{
                      backgroundColor: color + "20",
                      color: color
                    }}
                  >
                    <Description />
                  </div>
                  <div className="category-details">
                    <Typography variant="h6" className="category-name">
                      {category}
                    </Typography>
                    <Typography variant="body2" className="category-stats">
                      {fileCount} documents • {fieldCount} fields extracted
                    </Typography>
                  </div>
                </div>
                <IconButton size="small">
                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </div>

              {/* Category Content */}
              <Collapse in={isExpanded}>
                <div className="category-content">
                  <div className="files-section">
                    {Object.entries(categoryData).map(
                      ([fileIndex, fileData]) => {
                        const fileKey = `${category}-${fileIndex}`;
                        const isFileExpanded = expandedFiles[fileKey];

                        return (
                          <Card key={fileKey} className="file-card">
                            {/* File Header */}
                            <div
                              className="file-header"
                              onClick={() => toggleFile(fileKey)}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="file-info">
                                <Chip
                                  label={`${category} ${
                                    parseInt(fileIndex) + 1
                                  }`}
                                  variant="outlined"
                                  size="small"
                                  className="file-badge"
                                />
                                <Typography
                                  variant="body2"
                                  className="param-count"
                                >
                                  {Object.keys(fileData).length} parameters
                                </Typography>
                              </div>
                              <IconButton size="small">
                                {isFileExpanded ? (
                                  <ExpandLess />
                                ) : (
                                  <ExpandMore />
                                )}
                              </IconButton>
                            </div>

                            {/* File Parameters */}
                            <Collapse in={isFileExpanded}>
                              <div className="file-parameters">
                                <div className="parameters-grid">
                                  {Object.entries(fileData).map(
                                    ([key, value]) => (
                                      <div key={key} className="parameter-item">
                                        <Typography
                                          variant="caption"
                                          className="parameter-label"
                                        >
                                          {key}
                                        </Typography>
                                        <div className="parameter-value">
                                          <Typography variant="body2">
                                            {value}
                                          </Typography>
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            </Collapse>
                          </Card>
                        );
                      }
                    )}
                  </div>
                </div>
              </Collapse>
            </Card>
          );
        })}
      </div>

      {/* Info Message */}
      <div className="info-message">
        <Typography variant="body2">
          <strong>Batch Quality Check:</strong> Click the &quot;Run Batch
          Quality Check&quot; button above to perform a comprehensive quality
          assessment across all extracted data.
        </Typography>
      </div>
    </div>
  );
};

export default BatchExtractionResults;
